/**
 * One send's Clay re-check (plan 2026-09-27-003, U4/U6; PR #1715 review,
 * finding 6).
 *
 * When a send card mounts with rows the status poll gave up on
 * (`rowsNeedingRecheck` — the shared `isRecheckEligible` rule), ask the server
 * to re-read them from Clay, then refresh the send history. "Check again"
 * re-asks on demand.
 *
 * A MUTATION, not a query: the call is a POST that writes Clay's answer to the
 * log. It used to be a `useQuery` (so StrictMode's second mount would join the
 * in-flight request), which also meant a failure went nowhere — no toast, and
 * a query error the UI never read. Now a failure is an error toast with the
 * server's own `problem()` message when there is one (`rowActionErrorMessage`,
 * the same helper Retry uses) — never a status code.
 *
 * Once per REAL mount, guarded by `send-recheck-store.ts` (state, not a ref —
 * see its header and docs/v4-system/strictmode-resilient-effects.md):
 * StrictMode's second setup finds the dispatch `'checking'` and joins; a later
 * remount finds it `'checked'` and checks again. The POST is read-and-merge
 * only and single-flighted server-side too.
 */
import { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addErrorToast } from '@clay-run/glaze/toast/toast-queue';

import { WriteRefusedError } from '../../../../_shared/fetch-error';
import { useApi } from '../../../../_shared/hooks';
import { recheckDispatchRequest } from '../../../api-client';
import { playSendsQueryKey } from '../../../dispatch-inspector-logic';
import { claimAutoRecheck, settleAutoRecheck } from '../../../send-recheck-store';
import { rowActionErrorMessage } from '../../../settings-page-logic';

export interface SendRecheck {
  /** A re-check request is in flight. */
  checking: boolean;
  /** Ask Clay again now ("Check again"). */
  checkAgain: () => void;
}

const RECHECK_FAILED_TOAST = "Couldn't check with Clay — try again in a minute.";

export function useSendRecheck(
  playId: string,
  dispatchId: string | null,
  /** The send summary's `needsRecheck` — how many people the re-check would ask Clay about (U0a: the rows are paged, so the server counts). */
  staleCount: number,
): SendRecheck {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const needsRecheck = dispatchId !== null && staleCount > 0;

  const { mutate, isPending } = useMutation({
    mutationFn: () => recheckDispatchRequest(apiFetch, playId, { dispatchId: dispatchId ?? '' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: playSendsQueryKey(playId) }),
    // The failure toast is ours (the server's own message), so the global MutationCache toast is off.
    meta: { suppressErrorToast: true },
    onError: (err) => {
      // apiFetch already announced a refused write.
      if (err instanceof WriteRefusedError) return;
      addErrorToast({ content: rowActionErrorMessage(err, RECHECK_FAILED_TOAST) });
    },
    onSettled: () => {
      if (dispatchId !== null) settleAutoRecheck(dispatchId);
    },
  });

  useEffect(() => {
    if (dispatchId === null || !needsRecheck) return;
    if (!claimAutoRecheck(dispatchId)) return;
    mutate();
  }, [dispatchId, needsRecheck, mutate]);

  return {
    checking: isPending,
    checkAgain: () => {
      if (dispatchId !== null && !isPending) mutate();
    },
  };
}
